import React, { forwardRef, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import moment from 'moment/moment'
import PropTypes from 'prop-types'

import compact from 'lodash/compact'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import Grid from '@material-ui/core/Grid'

import projectSchema from '@galo-monitor/forms/schemas/company/project.schema'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { selectUser } from '@galo-monitor/stores/authentication/selectorAuthentication'
import { selectLanguage } from '@galo-monitor/stores/language/selectorLanguage'
import { ProjectActions } from '@galo-monitor/stores/project'
import { momentBackDateFormat } from '@galo-monitor/utils/dates'
import Form from '@galo-monitor/web-components/Form'
import InputDate from '@galo-monitor/web-components/InputDate'
import InputText from '@galo-monitor/web-components/InputText'
import CustomInputSearch from '@galo-monitor/web-containers/forms/property/ActivityForm/components/CustomInputSearch'

import { Container } from './styles'

const ProjectForm = forwardRef((props, formRef) => {
  const { project, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()
  const { selected } = useSelector(selectLanguage)
  const { id: loggedUserId } = useSelector(selectUser)

  const { data: clientCompanies } = useGaloMonitorApi('/companies/clients_companies/list')
  const { data: users } = useGaloMonitorApi('/companies/users')

  const companiesOptions = useMemo(
    () => !isEmpty(clientCompanies)
      ? map(clientCompanies, ({ id, name }) => ({
        label: name,
        value: id.toString()
      }))
      : [],
    [clientCompanies]
  )

  const usersOptions = useMemo(
    () => !isEmpty(users)
      ? map(users, ({ id, name }) => ({
        label: name,
        value: id.toString()
      }))
      : [],
    [users]
  )

  const isUpdate = useMemo(() => !!project.id, [project.id])

  const handleSubmit = useCallback(
    data => {
      const {
        clientCompanies: clientCompaniesData,
        users: usersData
      } = data

      const formattedClientCompanies = map(clientCompaniesData, client => ({
        'client_company_id': Number(client)
      }))

      const formattedUsers = compact(
        map(usersData, user =>
          !isUpdate && Number(user) === loggedUserId
            ? null
            : { 'user_id': Number(user) }
        )
      )

      setLoading(true)
      dispatch(ProjectActions.saveProject(
        {
          ...project,
          ...data,
          clientCompanies: formattedClientCompanies,
          users: formattedUsers
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('{this} account', {
              howMany: 1,
              this: t('project', { howMany: 1 })
            }),
            action: toLower(t(isEmpty(project) ? 'created' : 'updated', { gender: 'male', howMany: 1 }))
          }))
          onSuccess(data)
        },
        onError
      ))
    },
    [dispatch, isUpdate, loggedUserId, onError, onSuccess, project, setLoading, snackbar, t]
  )

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ projectSchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <Grid container spacing={ 2 }>
          <Grid item xs={ 12 }>
            <InputText
              name="name"
              label={ t('name of the project') }
              defaultValue={ project.name }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 }>
            <CustomInputSearch
              name="clientCompanies"
              label={ t('company client', { howMany: 1 }) }
              options={ companiesOptions }
              defaultValue={ map(project.clientCompanies, ({ id }) => id.toString()) }
              disabled={ loading }
              multiple
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputDate
              label={ t('start date') }
              name="start"
              defaultValue={ moment(project.start).format(momentBackDateFormat) }
              locale={ selected }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputDate
              label={ t('end date') }
              name="finish"
              defaultValue={ moment(project.finish).format(momentBackDateFormat) }
              locale={ selected }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 }>
            <CustomInputSearch
              name="users"
              label={ t('inform the collaborators') }
              options={ usersOptions }
              defaultValue={ [loggedUserId.toString()] }
              disabled={ loading }
              multiple
              fullWidth
            />
          </Grid>
        </Grid>
      </Container>
    </Form>
  )
})

ProjectForm.propTypes = {
  project: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

ProjectForm.defaultProps = {
  project: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default ProjectForm
